import { Button } from "@progress/kendo-react-buttons";
import {
  Link,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  TabStrip,
  TabStripSelectEventArguments,
  TabStripTab,
} from "@progress/kendo-react-layout";
import Transcription from "./Transcription";
import CallSummary from "./CallSummary";
import jwt_decode from "jwt-decode";
import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
  Suspense,
} from "react";
import CallHistory from "./CallHistory";
import { CallDetail } from "../../types/call";
import ConversationFlow from "./ConversationFlow";
import { Loader } from "@progress/kendo-react-indicators";
import RecordingScorecardTab from "./RecordingScorecardTab";
import ContentPlaceholder from "../../components/ContentPlaceholder";
import RecordingComments from "./RecordingComments";
import { useNavigate } from "react-router-dom";
import RecordingTrack from "./RecordingTrack";
import {
  extractExactMatchTopicsInsights,
  extractSegmentSentimentInsights,
  extractTranscriptInsights,
} from "../../utils/trackInsightUtils";
import {
  Comment,
  RecordingMoment,
  RecordingSegmentSentiment,
  RecordingTopic,
  RecordingTranscription,
} from "../../types/recording";
import { AxiosError } from "axios";
import { axiosWithAuth } from "../../utils/customAxios";
import FileDownload from "js-file-download";
import { RecordingTrackSegment } from "../../types/player-track";
import "./CallDetails.scss";
//import CallPlayer from "../../components/custom/CallPlayer";
import CustomUserInfo from "../../components/custom/CustomUserInfo";
import { formatDateTime, formatSecondsToTime } from "../../utils/dateTimeUtils";
import { Popup } from "@progress/kendo-react-popup";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { AccessPermissionEnum } from "../../enums/accessPermissionEnum";
import useAuth from "../../hooks/useAuth";
import ShareDialog from "../../components/ShareDialog";
import callsService from "../../services/calls.service";
import recordingsService from "../../services/recordings.service";
import useLocale from "../../hooks/useLocale";
import { Dictionary } from "../../types/Dictionary";
import { navData } from "../../data/NavigationData";
import CallAdditionalInfoDialog from "./CallAdditionalInfoDialog";
import Alert from "../../components/custom/Alert";
import { IsValidString } from "../../utils/stringUtils";
import CallFlag from "../../components/custom/CallFlag";
import { ColorCategory } from "../../types/master-data";
import { TinyUser, User } from "../../types/user";
import usersService from "../../services/users.service";
import useSwal from "../../hooks/useSwal";
import { SweetAlertOptions } from "sweetalert2";
import AuditLogSettings from "../settings/system-settings/audit-log-settings/AuditLogSettings";

import IcsLogo from "../../assets/images/IcsLogo.png";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import products from "./callLegs.json";
import { RecordingLockedSegment } from "../../types/recording/RecordingLockedSegment";
import ParticipantPopup from "../../components/custom/ParticipantPopup";
import useMasterData from "../../hooks/useMasterData";
import { load } from "@progress/kendo-react-intl";
import { TokenPayload } from "../../types/context";
import useSessionStorage from "../../hooks/useSessionStorage";
import LoadingOverlay from "../../components/LoadingOverlay";
import useTranslation from "../../hooks/useTranslation";
import { parseJSON } from "../../utils/customJsonParser";
import { RecordingCallDetailValues } from "../../types/recording/RecordingCallDetailValues";
import { duration } from "moment";
import { RecordingSummary } from "../../types/recording/RecordingSummary";
import RecordingSummaryTab from "./RecordingSummaryTab";
import TranscriptionTab from "./TranscriptionTab";
import { RecordingAction } from "../../types/recording/RecordingAction";
import { RecordingSegment } from "../../types/recording/RecordingSegment";
import { NumberLike } from "@visx/scale";
import WaveSurfer from "wavesurfer.js";
import { ErrorBoundary } from "react-error-boundary";
import fallbackRender from "../../components/ErrorBoundary";
const CallPlayer = React.lazy(
  () => import("../../components/custom/CallPlayer")
);
export interface RegionSelection {
  start: number;
  end: number;
}

export interface CommentRegionSelection {
  commentId: number;
  segment: RecordingSegment;
}

export interface LockedRegionSelection {
  segment: RecordingSegment;
  isLocked: boolean;
  isSystemLocked?: boolean;
}

interface CallDetailsProps {
  sharedProp?: boolean;
}

export interface kendoAlertData {
  popup: boolean;
  style: any;
  message: string;
}

const topicRelevanceColor = (t: RecordingTopic) => {
  var topicColor = "bg-yellowDLL";
  if (t.topicRelevance === null) {
    topicColor = "bg-greenDarkDLL";
  } else if (t.topicRelevance > 0.9) {
    topicColor = "bg-greenDLL";
  } else if (t.topicRelevance > 0.6) {
    topicColor = "";
  }
  return topicColor;
};

const CallDetails: React.FC<CallDetailsProps> = ({ sharedProp }) => {
  const trans = useTranslation("CallDetail");
  const navigate = useNavigate();
  const location = useLocation();
  const localeCtx = useLocale();
  const swal = useSwal();
  const auth = useAuth();
  const params = useParams();
  const masterData = useMasterData();
  const { platformCallDetailId } = params;
  const [searchParams, setSearchParams] = useSearchParams();
  const recordingId = searchParams.get("recordingId");
  const selectedTabViaNavigation = searchParams.get("selectedtab");
  const waveform = useRef<WaveSurfer | null>(null);

  const [shared, setShared] = useState<boolean>(sharedProp ?? false);
  const [selectedTab, setSelectedTab] = useState(
    selectedTabViaNavigation && selectedTabViaNavigation.trim() !== ""
      ? parseInt(selectedTabViaNavigation)
      : 0
  );
  const [regionSelection, setRegionSelection] = useState<RegionSelection>({
    start: 0,
    end: 0,
  });
  const [recordingDuration, setRecordingDuration] = useState<number>(0);
  const [playbackProgress, setPlaybackProgress] = useState<number>(0);
  const [callDetail, setCallDetail] = useState<CallDetail | undefined>();
  const [callDetailLoading, setCallDetailLoading] = useState<boolean>(true);
  const [callDetailError, setCallDetailError] = useState<string | undefined>();
  const [showUpgradeLicense, setShowUpgradeLicense] = useState<boolean>(false);
  const [isDiscarded, setIsDiscarded] = useState<boolean>(false);
  const [rawTranscript, setRawTranscript] = useState<
    RecordingTranscription | undefined
  >();
  const [rawTranscriptLoading, setRawTranscriptLoading] =
    useState<boolean>(true);
  const [rawTranscriptError, setRawTranscriptError] = useState<
    string | undefined
  >();
  // const [recMoments, setRecMoments] = useState<RecordingMoment[] | undefined>();
  // const [recMomentsLoading, setRecMomentsLoading] = useState<boolean>(true);
  // const [recMomentsError, setRecMomentsError] = useState<string | undefined>();
  const [recTopics, setRecTopics] = useState<RecordingTopic[] | undefined>();
  const [recTopicsLoading, setRecTopicsLoading] = useState<boolean>(true);
  const [recTopicsError, setRecTopicsError] = useState<string | undefined>();
  const [recSentiments, setRecSentiments] = useState<
    RecordingSegmentSentiment[] | undefined
  >();
  const [recSentimentsLoading, setRecSentimentsLoading] =
    useState<boolean>(true);
  const [recSentimentsError, setRecSentimentsError] = useState<
    string | undefined
  >();
  const [participantsTrackInsight, setParticipantsTrackInsight] = useState<
    RecordingTrackSegment[] | undefined
  >();
  const [exactMatchTopicInsights, setExactMatchTopicsTrackInsight] = useState<
    RecordingTrackSegment[] | undefined
  >();
  const [exactMatchTopic, setExactMatchTopic] = useState<
    RecordingTopic[] | undefined
  >();
  const [sentimentsTrackInsight, setSentimentsTrackInsight] = useState<
    RecordingTrackSegment[] | undefined
  >();
  const [additionalInfoDialogVisible, setAdditionalInfoDialogVisible] =
    useState<boolean>(false);
  const [recordingComments, setRecordingComments] = useState<
    Comment[] | undefined
  >();
  const [recordingCommentsError, setRecordingCommentsError] = useState<
    string | undefined
  >();
  const [commentRegions, setCommentRegions] = useState<
    CommentRegionSelection[]
  >([]);
  const [highlightCommentRegion, setHighlightCommentRegion] = useState<
    Comment | undefined
  >();
  const [lockedRegions, setLockedRegions] = useState<
    LockedRegionSelection[] | undefined
  >();
  const [audioLoaded, setAudioLoaded] = useState<boolean>(false);
  const [alertData, setAlertData] = useState<kendoAlertData>({
    popup: false,
    style: "",
    message: "",
  });
  const [trackSpanTime, setTrackSpanTime] = useState<number | undefined>();
  const [shareDialogVisible, setShareDialogVisible] = useState<boolean>(false);
  const [audioLoadedErrored, setAudioLoadedErrored] = useState<boolean>(false);
  const [recordingTrackLineClicked, setRecordingTrackLineClicked] =
    useState<boolean>(false);
  const [currentUserData, setCurrentUserData] = useState<User>();
  const [isMarkedForRetention, setIsMarkedForRetention] =
    useState<boolean>(false);
  const [totalCommentsTemp, setTotalCommentsTemp] = useState<
    number | undefined
  >(undefined);
  const [visible, setVisible] = useState<boolean>(false);
  const [commentShared, setCommentShared] = useState<boolean>(true);
  const [evalShared, setEvalShared] = useState<boolean>(true);
  const [outcomeShared, setOutcomeShared] = useState<boolean>(true);
  const [flagShared, setFlagShared] = useState<boolean>(true);
  const [participantHistoryShared, setParticipantHistoryShared] =
    useState<boolean>(true);
  const [auditLogsShared, setAuditLogsShared] = useState<boolean>(true);
  const [sharedSum, setSharedSum] = useState<number>(0);
  const [loadCallDetail, setLoadCallDetail] = useState<boolean>(
    sharedProp === true ? false : true
  );
  const [uniqueParticipants, setUniqueParticipants] = useState<TinyUser[]>();
  const [downloadingRecording, setDownloadingRecording] =
    useState<Boolean>(false);
  const [downloadVideoShow, setDownloadVideoShow] = useState<boolean>(false);
  const downloadVideoAnchor = useRef<any>();
  const [summary, setSummary] = useState<RecordingSummary>();
  const [processedCallAddiionalInfo, setProcessedCallAdditionalInfo] =
    useState<Dictionary<string>>();
  const [actions, setActions] = useState<RecordingAction[]>();
  const [highlightedCommentId, setHighlightedCommentId] = useState<number>(0);
  const [downloadDisabled, setDownloadDisabled] = useState<boolean>(true);

  const toggleDialog = () => {
    setVisible(!visible);
  };

  useEffect(() => {
    if (
      !localeCtx?.selectedLocale?.current.componentTranslations["CallDetail"]
    ) {
      trans.fetchTranslations("CallDetail");
    }
  }, [localeCtx?.selectedLocale]);

  useEffect(() => {
    const uniqueParticipantsTemp = callDetail?.secondParties.filter(
      (value: any, index: number, self: any) => {
        const key = value.email ?? value.phoneNo;

        return (
          index ===
          self.findIndex((obj: any) => {
            const otherKey = obj.email ?? obj.phoneNo;
            return otherKey === key;
          })
        );
      }
    );

    setUniqueParticipants(uniqueParticipantsTemp);
  }, [callDetail]);

  useEffect(() => {
    if (!recordingId) {
      navigate("/not-found", { replace: true });
    }
    if (shared) {
      const shareToken = sessionStorage.getItem("ShareToken");
      if (shareToken) {
        const decodedToken = jwt_decode<TokenPayload>(shareToken);
        if (decodedToken) {
          setSharedSum(decodedToken?.SharedSum);
          //if internally shared resource
          if (!decodedToken.GuestUserEmail) {
            checkInternalUserCallDetailAccess();
          } else {
            setLoadCallDetail(true);
          }
        }
      }
    }
  }, []);

  useEffect(() => {
    if (loadCallDetail) {
      if (!recordingId) {
        navigate("/not-found", { replace: true });
      }
      if (shared) {
        if (Number(sharedSum & 1) === 0) {
          setCommentShared(false);
        }
        if (Number(sharedSum & 2) === 0) {
          setEvalShared(false);
        }
        if (Number(sharedSum & 4) === 0) {
          setOutcomeShared(false);
        }
        if (Number(sharedSum & 8) === 0) {
          setParticipantHistoryShared(false);
        }
        if (Number(sharedSum & 16) === 0) {
          setAuditLogsShared(false);
        }
        if (Number(sharedSum & 32) === 0) {
          setFlagShared(false);
        }
      }
    }
  }, [
    recordingId,
    platformCallDetailId,
    navigate,
    searchParams,
    recordingDuration,
    loadCallDetail,
    masterData?.data,
  ]);

  useEffect(() => {
    getCallDetailValues();
    //getRecordingActions();
  }, [recordingId, shared]);

  useEffect(() => {
    sendFetchCallDetailRequest();
  }, [platformCallDetailId]);

  useEffect(() => {
    if (loadCallDetail) {
      if (rawTranscript && recordingDuration > 0) {
        setParticipantsTrackInsight(
          extractTranscriptInsights(
            rawTranscript,
            recordingDuration,
            trans.fetchLabelKeyTranslation
          )
        );
      }
      setSentimentsTrackInsight(
        extractSegmentSentimentInsights(recSentiments ? recSentiments : [])
      );
    }
  }, [
    rawTranscript,
    recSentiments,
    recTopics,
    recordingDuration,
    loadCallDetail,
  ]);

  useEffect(() => {
    if (loadCallDetail) {
      if (alertData.popup) {
        setTimeout(() => {
          setAlertData({ ...alertData, popup: false });
        }, 5000);
      }
    }
  }, [alertData.popup, loadCallDetail]);

  useEffect(() => {
    if (loadCallDetail) {
      const getCurrentUser = async () => {
        try {
          if (callDetail) {
            const currentUser = await usersService.getUser(
              callDetail?.firstParty?.id?.toString()
            );
            setCurrentUserData(currentUser);
          }
        } catch (err) {
          console.error(err);
        }
      };
      if (!shared || (shared && auth?.accessToken)) {
        getCurrentUser();
      }
    }
  }, [callDetail, shared, loadCallDetail]);

  const checkForShowAuditLogs = () => {
    if (!shared) {
      return auth?.checkUserAccess(AccessPermissionEnum.ManageAccount);
    } else {
      var shareToken = window.sessionStorage.getItem("ShareToken");
      if (shareToken) {
        const decodedToken = jwt_decode<TokenPayload>(shareToken);
        const permissions = parseJSON<number[]>(decodedToken.AccessPermissions);
        return permissions?.includes(AccessPermissionEnum.ManageAccount);
      } else {
        return false;
      }
    }
  };

  const handleSelect = (e: TabStripSelectEventArguments) => {
    setSelectedTab(e.selected);
  };

  const highlightComment = (commentId: number) => {
    setHighlightedCommentId(commentId);
    var commentElement = document.getElementById(commentId.toString());
    commentElement?.scrollIntoView({ behavior: "smooth" });
    setSelectedTab(1);
  };

  const checkInternalUserCallDetailAccess = async () => {
    try {
      const response = await callsService.checkUserCallDetailAccess(
        platformCallDetailId ?? ""
      );
      if (response) {
        setShared(false);
      }
      setLoadCallDetail(true);
    } catch (err) {
      if (err instanceof AxiosError) {
        setCallDetailError(err.message);
        if (err.response?.status === 501) {
          navigate("/error", { replace: true });
        } else if (err.response?.status === 404) {
          navigate("/not-found", { replace: true });
        } else {
          navigate("/unauthorized-calldetail", { replace: true });
        }
      }
    }
  };

  const processCallAdditionalInfo = () => {
    var tempDic: Dictionary<string> = callDetail?.callAdditionalInfo ?? {};
    const licenseName: string | undefined =
      masterData?.data?.licensePackages.find(
        (x) => x.localizationKey === callDetail?.licenseLocalizationKey
      )?.localizationValue ??
      masterData?.data?.licensePackages.find(
        (x) =>
          x.baseLicense?.localizationKey === callDetail?.licenseLocalizationKey
      )?.baseLicense.localizationValue;
    tempDic = {
      ...tempDic,
      licenseName: licenseName ?? "",
    };
    console.log(licenseName);
    console.log(masterData?.data?.licensePackages);
    if (callDetail?.callTime) {
      var expiry = new Date(callDetail?.callTime);
      if (expiry) {
        if (callDetail?.storageDuration.durationType.toLowerCase() == "year") {
          expiry?.setUTCFullYear(
            expiry.getUTCFullYear() + callDetail.storageDuration.duration
          );
        } else if (
          callDetail?.storageDuration.durationType.toLowerCase() == "month"
        ) {
          expiry?.setUTCMonth(
            expiry.getUTCMonth() + callDetail.storageDuration.duration
          );
        } else if (
          callDetail?.storageDuration.durationType.toLowerCase() == "day"
        ) {
          expiry.setUTCDate(
            expiry.getUTCDate() + callDetail.storageDuration.duration
          );
        }

        tempDic = { ...tempDic, expiryDate: expiry.toDateString() };
      }
    }

    setProcessedCallAdditionalInfo(tempDic);
  };

  useEffect(() => {
    processCallAdditionalInfo();
  }, [callDetail, masterData?.data?.licensePackages]);

  const getCallDetailValues = async () => {
    try {
      setRawTranscriptLoading(true);
      setRecTopicsLoading(true);
      setRecSentimentsLoading(true);

      const data = await recordingsService.fetchRecordingCallDetailValues(
        parseInt(recordingId!),
        shared === true
      );
      sendFetchTranscriptionRequest(data.transcription);
      sendFetchRecTopicsRequest(data.topics);
      sendFetchRecSegmentSentimentsRequest(data.segmentSentiments);
      sendFetchRecordingCommentsRequest(data.comments);
      sendFetchRecordingLockedSegmentsRequest(data.lockedSegments);
      sendRecordingSummary(data.summary);
    } catch (err) {
      if (err instanceof Error) {
        if (err instanceof AxiosError) {
          setRawTranscriptError(
            err?.response?.data?.error ? err.response.data.error : err.message
          );
        }
      }
    }
  };

  const getRecordingActions = async () => {
    try {
      const data = await recordingsService.fetchRecordingActions(
        parseInt(recordingId!),
        shared === true
      );
      sendRecordingActions(data);
    } catch (err) {
      if (err instanceof Error) {
        if (err instanceof AxiosError) {
          console.log(err);
        }
      }
    }
  };

  const sendRecordingActions = (actions: RecordingAction[]) => {
    setActions(actions);
  };

  const sendFetchCallDetailRequest = async () => {
    try {
      setCallDetailLoading(true);
      const data = await callsService.fetchCallByPlatformCallDetailId(
        platformCallDetailId ? platformCallDetailId : "",
        shared === true
      );
      if (recordingId && parseInt(recordingId) !== data.recordings[0].id) {
        navigate("/unauthorized-calldetail", { replace: true });
      }
      setCallDetail(data);
      setShowUpgradeLicense(!data.recordings[0].isAIAnalysed);
      setIsMarkedForRetention(data.recordings[0].isMarkedForRetention);
      setTotalCommentsTemp(data.recordings[0].totalComments);
      setIsDiscarded(data?.status === "Discarded");
    } catch (err) {
      if (err instanceof AxiosError) {
        setCallDetailError(err.message);
        if (err.response?.status === 501) {
          navigate("/error", { replace: true });
        } else if (err.response?.status === 404) {
          navigate("/not-found", { replace: true });
        } else {
          navigate("/unauthorized-calldetail", { replace: true });
        }
      }
    } finally {
      setCallDetailLoading(false);
      setRawTranscriptLoading(false);
      // setRecMomentsLoading(false);
      setRecTopicsLoading(false);
      setRecSentimentsLoading(false);
    }
  };

  const sendFetchTranscriptionRequest = (
    transcription: RecordingTranscription
  ) => {
    setRawTranscript(transcription);
  };

  const sortingFunc = (p1: RecordingTopic, p2: RecordingTopic) => {
    return Math.round(p1.topicRelevance * 100) <
      Math.round(p2.topicRelevance * 100)
      ? 1
      : Math.round(p1.topicRelevance * 100) >
        Math.round(p2.topicRelevance * 100)
      ? -1
      : 0;
  };

  const sendFetchRecTopicsRequest = (topics: RecordingTopic[]) => {
    const extendedSearchTopic = topics.filter((t) => t.topicRelevance !== null);
    const sortedextendedSearchTopic = extendedSearchTopic.sort(sortingFunc);
    setRecTopics(sortedextendedSearchTopic);
    setExactMatchTopic(topics?.filter((t) => t.topicRelevance === null));
  };

  useEffect(() => {
    if (exactMatchTopic) {
      setExactMatchTopicsTrackInsight(
        extractExactMatchTopicsInsights(exactMatchTopic, recordingDuration)
      );
    }
  }, [exactMatchTopic, recordingDuration]);

  const sendFetchRecSegmentSentimentsRequest = (
    segments: RecordingSegmentSentiment[]
  ) => {
    if (segments) {
      setRecSentiments(segments);
      setSentimentsTrackInsight(extractSegmentSentimentInsights(segments));
    }
  };

  const sendFetchRecordingCommentsRequest = (comments: Comment[]) => {
    setRecordingComments(comments);
    // adding comment regions
    const regionsComm: CommentRegionSelection[] = [];
    comments.forEach((c) => {
      if (c.recordingSegment) {
        regionsComm.push({
          commentId: c.id,
          segment: c.recordingSegment,
        });
      }
    });
    setCommentRegions(regionsComm);
  };
  const sendFetchRecordingLockedSegmentsRequest = (
    lockedSegments: RecordingLockedSegment[]
  ) => {
    // adding locked regions
    let regionsLocked: LockedRegionSelection[] = [];
    if (lockedSegments) {
      lockedSegments.forEach((c) => {
        if (
          typeof c.recordingSegment.startTime !== "undefined" &&
          typeof c.recordingSegment.startTime !== null &&
          typeof c.recordingSegment.startTime === "number" &&
          typeof c.recordingSegment.startTime !== "undefined" &&
          typeof c.recordingSegment.startTime !== null &&
          typeof c.recordingSegment.startTime === "number"
        ) {
          regionsLocked.push({
            segment: c.recordingSegment,
            isLocked: true,
            isSystemLocked: c.isSystemLocked,
          });
        }
      });
    }
    setLockedRegions(regionsLocked);
  };

  const sendRetainRecordingRequest = async () => {
    try {
      const response = await recordingsService.retainRecording(
        parseInt(recordingId!),
        !isMarkedForRetention,
        shared === true
      );
      if (response) {
        setIsMarkedForRetention((prev) => !prev);
      }
    } catch (err) {
      const swalOptions: SweetAlertOptions = {
        icon: "error",
        title: "Error",
        text: "Failed to Hold/Release recording. Please try again.",
      };
      swal.fire(swalOptions);
    }
  };

  const sendDeleteRecordingRequest = async () => {
    const swalOptions: SweetAlertOptions = {
      icon: "info",
      title: `${trans.fetchLabelKeyTranslation(
        "SwtAltCallDeletedTitle",
        "Are you sure you want to delete this recording?"
      )}`,
      text: `${trans.fetchLabelKeyTranslation(
        "SwtAltCallDeletedText",
        "This recording will be deleted"
      )}`,
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: `${trans.fetchLabelKeyTranslation(
        "SwtAltConfirmToDeleteCallButtonText",
        "Delete"
      )}`,
      cancelButtonText: `${trans.fetchLabelKeyTranslation(
        "CancelButtonText",
        "Cancel"
      )}`,
    };
    const resp = await swal.fire(swalOptions);
    if (resp.isConfirmed) {
      try {
        const response = await recordingsService.deleteRecording(
          parseInt(recordingId!),
          shared === true
        );
        if (response) {
          const swalOptions: SweetAlertOptions = {
            icon: "success",
            title: "Recording deleted.",
            text: "Recording has been deleted successfully.",
          };
          swal.fire(swalOptions).then(async (result) => {
            if (result.isConfirmed) {
              window.close();
            }
          });
        }
      } catch (err) {
        const swalOptions: SweetAlertOptions = {
          icon: "error",
          title: "Error",
          text: "Failed to delete recording. Please try again.",
        };
        swal.fire(swalOptions);
      }
    }
  };

  const sendRecordingSummary = (summary: RecordingSummary) => {
    setSummary(summary);
  };

  const onUpdateCallDetailProperties = (updatedCallDetail: CallDetail) => {
    setCallDetail(updatedCallDetail);
  };

  const onScorecardEvaluated = useCallback(() => {
    sendFetchCallDetailRequest();
  }, []);

  const onScorecardEvalDeleted = () => {
    sendFetchCallDetailRequest();
  };

  const onCommentMouseOver = (commentId: number) => {
    const comment = recordingComments?.find((c) => c.id === commentId);
    if (comment) {
      setHighlightCommentRegion(comment);
    }
  };

  const onCommentMouseOut = () => {
    setHighlightCommentRegion(undefined);
  };

  const onUserSeekOutOfCommentRegion = () => {
    setHighlightedCommentId(0);
  };

  const downloadRecording = async (isVideo: boolean) => {
    if (downloadingRecording) {
      return;
    }
    try {
      setDownloadingRecording(true);
      var response = await axiosWithAuth.get(
        `recording/${recordingId}/download?isVideo=${isVideo}`,
        { responseType: "arraybuffer" }
      );
      if (response.data) {
        if (isVideo) {
          FileDownload(response.data, `${recordingId}_callRecording.mp4`);
        } else {
          FileDownload(response.data, `${recordingId}_callRecording.wav`);
        }
      } else {
        const swalOptions: SweetAlertOptions = {
          icon: "error",
          title: "Error",
          text: "Recording download failed. Please try again.",
        };
        await swal.fire(swalOptions);
      }
    } catch (error) {
      const swalOptions: SweetAlertOptions = {
        icon: "error",
        title: "Error",
        text: "Recording download failed. Please try again.",
      };
      await swal.fire(swalOptions);
    } finally {
      setDownloadingRecording(false);
    }
  };

  const toggleShareDialogHandler = () => {
    setShareDialogVisible((prev) => !prev);
  };

  const onCloseShareDialog = () => {
    setShareDialogVisible(false);
  };

  const onRecordingTrackSpanPlay = (time: number) => {
    setTrackSpanTime(time);
    setRecordingTrackLineClicked(!recordingTrackLineClicked);
  };

  const onAddComment = (newComment: Comment) => {
    if (recordingComments) {
      setRecordingComments([newComment, ...recordingComments]);
      if (newComment.recordingSegment != null) {
        setCommentRegions([
          ...commentRegions,
          {
            commentId: newComment.id,
            segment: newComment.recordingSegment,
          },
        ]);
      }
    }
    setRegionSelection({
      start: 0,
      end: recordingDuration,
    });
  };

  const onDeleteComment = (commentId: number) => {
    if (recordingComments) {
      setRecordingComments((prevState) => {
        return prevState?.filter((c) => c.id !== commentId);
      });
      setCommentRegions((prevState) => {
        return prevState.filter((c) => c.commentId !== commentId);
      });
    }
  };

  const onUpdateComment = (updatedComment: Comment) => {
    if (recordingComments) {
      const updatedCommentsList: Comment[] = [];
      recordingComments.forEach((c) => {
        if (c.id === updatedComment.id) {
          updatedCommentsList.push(updatedComment);
        } else {
          updatedCommentsList.push(c);
        }
      });
      setRecordingComments(updatedCommentsList);
    }
  };

  const onDialogVisibleToggle = () => {
    setAdditionalInfoDialogVisible((prevState) => !prevState);
  };

  const handleFlagSelection = async (selectedFlag: ColorCategory) => {
    if (callDetail) {
      await callsService
        .createCallDetailCategory(callDetail.platformCallDetailId, selectedFlag)
        .then((response) => {
          setCallDetail({ ...callDetail, colorCategory: selectedFlag });
        })
        .catch((err) => {
          const swalOptions: SweetAlertOptions = {
            icon: "error",
            title: "Error",
            text: "Failed to update call flag. Please try again.",
          };
          swal.fire(swalOptions);
        });
    }
  };

  const toggleDownloadVideoShow = () => {
    setDownloadVideoShow((prevState) => !prevState);
  };

  const toggleDownloadVideoShowByBlur = () => {
    setDownloadVideoShow(false);
  };

  const IcsAnalytics = (props: { tab: any }) => {
    return (
      <>
        <div className="tabIcsAnalytics">
          <img className="tabIcsAnalyticsImg" src={IcsLogo} alt="" />
          <span>Analytics</span>
        </div>
      </>
    );
  };
  const CustomCellUser = (props: any) => (
    <>
      <td>
        <div className="d-flex align-items-center">
          <div className="tblUsr round-sm2 m-r-10">
            <div className="topUsrAreaPic">
              <div className="topUsrAreaPic-i">
                <div className="contact-list-icon bg-thistle">
                  <div className="contact-list-icon-txt">SR</div>
                </div>
              </div>
              <div className="topUsrAreaPic-tx">
                <span className="text-primary font-weight-bold-600 cursor-default text-decoration-none float-left w-100 line-height-1 text-left">
                  Steven Rosers
                </span>
              </div>
            </div>
          </div>
        </div>
      </td>
    </>
  );
  useEffect(() => {
    localStorage.setItem("callPlayerState", "callDetailsPage");
  }, []);

  const PlatformName = () => {
    const platformKey =
      masterData?.data?.platforms.find(
        (p) => p.name === callDetail?.platform?.name
      )?.localizationKey ??
      callDetail?.platform?.name ??
      "";
    const platform = masterData?.fetchMasterDataKeyTranslation(
      "Platform",
      platformKey,
      callDetail?.platform?.name ?? ""
    );
    return <>{platform}</>;
  };

  useEffect(() => {
    if (downloadVideoShow) {
      const body = document.getElementById("root");
      const eventHandler = () => {
        setDownloadVideoShow(false);
      };
      if (body != null) {
        body.addEventListener("click", eventHandler);
      }
      return () => body?.removeEventListener("click", eventHandler);
    }
  }, [downloadVideoShow]);

  const MemoizedRecordingScorecardTab = useMemo(() => {
    return (
      <RecordingScorecardTab
        isCallEvaluated={callDetail ? callDetail.isEvaluated : false}
        shared={shared}
        recordingId={parseInt(recordingId!)}
        onScorecardEvaluated={onScorecardEvaluated}
        allocatedScorecards={currentUserData?.scorecards ?? []}
        baseLicenseId={callDetail ? callDetail.recordings[0].baseLicenseId : 0}
        onScorecardEvalDeleted={onScorecardEvalDeleted}
      />
    );
  }, [callDetail, shared, recordingId, currentUserData]); // can add selectedTab here

  const playTranscription = (seconds: number) => {
    if (waveform.current) {
      waveform?.current.seekTo(seconds / waveform.current?.getDuration());
      waveform.current.play();
    }
  };

  return (
    <div className="call-details m-b-20">
      {trans.translationsLoading && (
        <LoadingOverlay
          customStyle={{ position: "fixed", marginTop: "55px" }}
          themeColor={"light"}
          size={"medium"}
          loadingText={trans.fetchLabelKeyTranslation("SwitchLanguageText", "")}
        />
      )}
      {additionalInfoDialogVisible && (
        <CallAdditionalInfoDialog
          platformCallDetailId={callDetail?.platformCallDetailId ?? ""}
          data={processedCallAddiionalInfo ?? {}}
          onDialogClose={onDialogVisibleToggle}
          fetchLabelKeyTranslation={trans.fetchLabelKeyTranslation}
        />
      )}
      <div className="row">
        <div className="col-md-12">
          <div className="card cardEffect callDetailBody mb-5">
            {alertData.popup && (
              <Alert setAlertData={setAlertData} alertData={alertData} />
            )}
            <div
              className="card-header bg-white d-flex justify-content-between callDetailHdr"
              style={{
                borderBottomColor: "rgba(0, 0, 0, 0.07)",
                overflow: "auto",
              }}
            >
              <span className="detailHeader d-flex align-items-center">
                <span className="fs-16 font-weight-semi p-r-20">
                  {`${trans.fetchLabelKeyTranslation(
                    "CallDetails",
                    "Call Details"
                  )}`}
                </span>
                {callDetail && (
                  <div className="listInr-detail d-flex">
                    <CustomUserInfo
                      userInfo={callDetail.firstParty!}
                      customPnlCss={"liDetail d-flex align-items-center p-r-5"}
                      customDataHeaderCss={
                        "liDetail-h font-semi-bold text-balck-10 fs-14"
                      }
                      customDataBodyCss={
                        "liDetail-p fs-13 text-black-8 line-height-1"
                      }
                    />
                    <CustomUserInfo
                      userInfo={
                        callDetail.secondParties.find(
                          (sp: any) => sp.id === 0
                        ) ?? callDetail.secondParties[0]
                      }
                      customPnlCss={
                        "liDetail d-flex align-items-center p-l-10 m-l-15 border-left-solid border-w-2 border-black-2"
                      }
                      customDataHeaderCss={"liDetail-h fs-13 text-black-14"}
                      customDataBodyCss={
                        "liDetail-p fs-13 text-black-8 line-height-1"
                      }
                      customDataHeaderLabel={
                        callDetail.secondParties.find((sp: any) => sp.id === 0)
                          ?.email ?? callDetail.secondParties[0].email
                      }
                      customDataBodyLabel={trans.fetchLabelKeyTranslation(
                        "TextRemoteParty",
                        "Remote Party"
                      )}
                    />
                    {callDetail.secondParties.length > 1 && (
                      <div className="moreDataInr align-items-center d-flex">
                        <Tooltip
                          anchorElement={"target"}
                          position={"top"}
                          parentTitle={true}
                        >
                          <div className="moreDataList">
                            <div
                              className="moreDataListBtn"
                              style={{ cursor: "pointer" }}
                              title={`${trans.fetchLabelKeyTranslation(
                                "lblMoreParticipants",
                                "More Participants"
                              )}`}
                              onClick={toggleDialog}
                            >
                              <i className="bi bi-caret-down-fill text-black-9"></i>
                            </div>
                            {visible && (
                              <ParticipantPopup
                                toggleDialog={toggleDialog}
                                participants={uniqueParticipants ?? []}
                              ></ParticipantPopup>
                            )}
                          </div>
                        </Tooltip>
                      </div>
                    )}
                    <div className="liDetail p-l-10 m-l-15 d-flex align-items-center border-left-solid border-w-2 border-black-2">
                      <div className="liDetail-data">
                        <div className="liDetail-h fs-13 text-balck-14">
                          <PlatformName />
                        </div>
                        <div className="liDetail-p fs-13 text-black-8 line-height-1">
                          {`${trans.fetchLabelKeyTranslation(
                            "TextPlatform",
                            "Platform"
                          )}`}
                        </div>
                      </div>
                    </div>
                    <div className="liDetail p-l-10 m-l-15 d-flex align-items-center border-left-solid border-w-2 border-black-2">
                      <div className="liDetail-data">
                        <div className="liDetail-h fs-13 text-balck-14">
                          {formatDateTime(
                            callDetail.callTime,
                            "MMM DD, YYYY, HH:mm"
                          )}
                        </div>
                        <div className="liDetail-p fs-13 text-black-8 line-height-1">
                          {`${trans.fetchLabelKeyTranslation(
                            "TextDateTime",
                            "Date/Time"
                          )}`}
                        </div>
                      </div>
                    </div>
                    <div className="liDetail p-l-10 m-l-15 d-flex align-items-center border-left-solid border-w-2 border-black-2">
                      <div className="liDetail-data">
                        <div className="liDetail-h fs-13 text-balck-14">
                          {formatSecondsToTime(callDetail.callDuration)}
                        </div>
                        <div className="liDetail-p fs-13 text-black-8 line-height-1">{`${trans.fetchLabelKeyTranslation(
                          "TitleDuration",
                          "Duration"
                        )}`}</div>
                      </div>
                    </div>
                    {flagShared && (
                      <div className="liDetail p-l-10 m-l-15 d-flex align-items-center border-left-solid border-w-2 border-black-2">
                        <div className="liDetail-data">
                          <div className="liDetail-h fs-13 text-balck-14">
                            <CallFlag
                              colorCategory={callDetail.colorCategory}
                              handleFlagSelection={handleFlagSelection}
                            />
                          </div>
                          <div className="liDetail-p fs-13 text-black-8">{`${trans.fetchLabelKeyTranslation(
                            "FieldCallFlag",
                            "Call Flag"
                          )}`}</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </span>
              {!shared && (
                <div className="callDetailAction d-flex align-items-center">
                  <Tooltip
                    anchorElement={"target"}
                    position={"top"}
                    parentTitle={true}
                  >
                    <div className="buttons-container hov-transparent d-flex align-itmes-center">
                      {auth?.checkUserAccess(
                        AccessPermissionEnum.ManageCompliance
                      ) &&
                        !isMarkedForRetention && (
                          <Button
                            className={`radius-50 iBtn-bg text-primary d-flex align-items-center justify-conter m-r-3`}
                            disabled={isDiscarded}
                          >
                            <span
                              className="fs-20 line-height-1"
                              title={`${trans.fetchLabelKeyTranslation(
                                "HoldRetainCallText",
                                "Hold/Retain Call"
                              )}`}
                            >
                              <i
                                onClick={sendRetainRecordingRequest}
                                className="bi bi-h-circle"
                              ></i>
                            </span>
                          </Button>
                        )}

                      {auth?.checkUserAccess(
                        AccessPermissionEnum.ManageCompliance
                      ) &&
                        isMarkedForRetention && (
                          <Button
                            className={`radius-50 iBtn-bg text-primary d-flex align-items-center justify-conter m-r-3`}
                            disabled={isDiscarded}
                          >
                            <span
                              className="fs-20 line-height-1"
                              title={trans.fetchLabelKeyTranslation(
                                "ReleaseCallText",
                                "Release"
                              )}
                            >
                              <i
                                onClick={sendRetainRecordingRequest}
                                className="bi bi-r-circle"
                              ></i>
                            </span>
                          </Button>
                        )}
                      {auth?.checkUserAccess(
                        AccessPermissionEnum.DeleteCalls
                      ) && (
                        <Button
                          className={`radius-50 iBtn-bg text-primary d-flex align-items-center justify-conter m-r-3
                      ${
                        isMarkedForRetention || shared
                          ? "bg-black-2 text-black-5 border-0"
                          : ""
                      }
                    `}
                          disabled={isMarkedForRetention || shared}
                        >
                          <span
                            className="fs-17 line-height-1"
                            title={`${trans.fetchLabelKeyTranslation(
                              "DeleteCallText",
                              "Delete Call"
                            )}`}
                          >
                            <i
                              onClick={sendDeleteRecordingRequest}
                              className="bi bi-trash3"
                            ></i>
                          </span>
                        </Button>
                      )}
                      <Button
                        className={
                          "radius-50 iBtn-bg text-primary d-flex align-items-center justify-conter m-r-3"
                        }
                      >
                        <span className="fs-20 line-height-1">
                          <i
                            onClick={onDialogVisibleToggle}
                            title={`${trans.fetchLabelKeyTranslation(
                              "TextAdditionalInformation",
                              "Additional Information"
                            )}`}
                            className="bi bi-info-circle"
                          ></i>
                        </span>
                      </Button>
                      {auth?.accessToken && (
                        <Button
                          className={`radius-50 iBtn-bg text-primary d-flex align-items-center justify-conter m-r-3 ${
                            auth?.checkUserAccess(
                              AccessPermissionEnum.ShareCalls
                            )
                              ? "iconBtn"
                              : "bg-black-2 text-black-5 border-0"
                          }`}
                          disabled={
                            !auth?.checkUserAccess(
                              AccessPermissionEnum.ShareCalls
                            )
                          }
                        >
                          <span className="fs-17 line-height-1">
                            <i
                              className="bi bi-share"
                              title={`${trans.fetchLabelKeyTranslation(
                                "TextShare",
                                "Share"
                              )}`}
                              onClick={toggleShareDialogHandler}
                            ></i>
                          </span>

                          {shareDialogVisible && callDetail && (
                            <ShareDialog
                              onClose={onCloseShareDialog}
                              resourceObjId={callDetail.id}
                              recordingPath={
                                window.location.pathname +
                                window.location.search
                              }
                            />
                          )}
                        </Button>
                      )}
                      {auth?.accessToken &&
                        (downloadingRecording ? (
                          <div className="radius-50 iBtn-bg text-primary d-flex align-items-center justify-content-center m-r-3">
                            <Loader type={"infinite-spinner"} size="small" />
                          </div>
                        ) : callDetail?.isVideoAvailable ? (
                          <div>
                            <Button
                              onClick={toggleDownloadVideoShow}
                              className={`radius-50 iBtn-bg text-primary d-flex align-items-center justify-conter m-r-3 ${
                                auth?.checkUserAccess(
                                  AccessPermissionEnum.DownloadCalls
                                )
                                  ? "iconBtn"
                                  : "bg-black-2 text-black-5 border-0"
                              }`}
                              title={"Download"}
                              disabled={
                                !auth?.checkUserAccess(
                                  AccessPermissionEnum.DownloadCalls
                                )
                              }
                            >
                              <span ref={downloadVideoAnchor}>
                                <i
                                  className="bi bi-download fs-16"
                                  style={{ lineHeight: "0" }}
                                ></i>
                              </span>
                            </Button>
                            <Popup
                              anchor={downloadVideoAnchor.current}
                              show={downloadVideoShow}
                              popupClass={"popup-content"}
                              style={{ margin: "10px 0 0 0" }}
                            >
                              <ul className="dropMenu droplist-ul">
                                <li key="DownloadAudio" className="droplist-li">
                                  <Button
                                    className="droplist-a justify-content-start px-2"
                                    onClick={() => {
                                      downloadRecording(false);
                                      setDownloadVideoShow(false);
                                    }}
                                    disabled={callDetail.status === "Discarded"}
                                  >
                                    {trans.translationsLoading
                                      ? "Download Audio"
                                      : trans.fetchLabelKeyTranslation(
                                          "Download Audio",
                                          "Download Audio"
                                        )}
                                  </Button>
                                </li>
                                <li key="DownloadVideo" className="droplist-li">
                                  <Button
                                    className="droplist-a justify-content-start px-2"
                                    onClick={() => {
                                      downloadRecording(true);
                                      setDownloadVideoShow(false);
                                    }}
                                    disabled={callDetail.status === "Discarded"}
                                  >
                                    {trans.translationsLoading
                                      ? "Download Video"
                                      : trans.fetchLabelKeyTranslation(
                                          "Download Video",
                                          "Download Video"
                                        )}
                                  </Button>
                                </li>
                              </ul>
                            </Popup>
                          </div>
                        ) : (
                          <Button
                            className={`radius-50 iBtn-bg d-flex text-primary align-items-center justify-content-center m-r-3 ${
                              auth?.checkUserAccess(
                                AccessPermissionEnum.DownloadCalls
                              )
                                ? "iconBtn"
                                : "bg-black-2 text-black-5 border-0"
                            }
                             `}
                            onClick={() => downloadRecording(false)}
                            disabled={
                              !auth?.checkUserAccess(
                                AccessPermissionEnum.DownloadCalls
                              ) ||
                              isDiscarded ||
                              downloadDisabled
                            }
                          >
                            <span className="fs-17 line-height-1">
                              <i
                                className="bi bi-download"
                                title={`${trans.fetchLabelKeyTranslation(
                                  "TextDownload",
                                  "Download"
                                )}`}
                              ></i>
                            </span>
                          </Button>
                        ))}
                    </div>
                  </Tooltip>
                </div>
              )}
            </div>
            {callDetail?.recordings[0].isAIAnalysed &&
              (
                <div className="row">
                  <div className="col-md-12">
                    <div className="detailTopic border-bottom-solid border-black-1 border-w-1 p-l-17 p-t-5 p-b-0 bg-white d-flex justify-content-between">
                      <span className="detailHeader d-flex align-items-center">
                        <span
                          className="fs-15 font-weight-semi p-r-20"
                          style={{ marginTop: "-5px" }}
                        >
                          {`${trans.fetchLabelKeyTranslation(
                            "TextTopics",
                            "Topics"
                          )}`}
                        </span>
                        <div className="p-r-15 p-l-15 bg-white w-100 float-left">
                          <div className="mx-th-tag">
                            <div className="mx-td-spn">
                              <div className="keywordRow">
                                {recTopics?.map(
                                  (topic: RecordingTopic, index: number) => (
                                    <div
                                      className={
                                        "keywordTrk " +
                                        topicRelevanceColor(topic)
                                      }
                                      key={index}
                                    >
                                      <Tooltip
                                        anchorElement={"target"}
                                        position={"top"}
                                        parentTitle={true}
                                      >
                                        <span
                                          title={
                                            topic.topicRelevance !== null
                                              ? `Relevance: ${Math.round(
                                                  topic.topicRelevance * 100
                                                )}%`
                                              : ""
                                          }
                                        >
                                          <i
                                            className={`bi ${
                                              topic.topicName.isPublic
                                                ? "bi-people"
                                                : "bi-lock-fill"
                                            } m-r-6`}
                                          ></i>
                                          {topic.topicName.name}
                                        </span>
                                      </Tooltip>
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </span>
                    </div>
                  </div>
                </div>
              )}
            <div className="card-body p-0">
              <div className="row">
                <div className="col-md-12">
                  <div className="p-t-10 p-r-15 p-b-0 p-l-15 bg-white w-100 float-left">
                    <ErrorBoundary FallbackComponent={fallbackRender}>
                      <Suspense
                        fallback={
                          <div>
                            <LoadingOverlay
                              customStyle={{
                                position: "fixed",
                                marginTop: "55px",
                              }}
                              themeColor={"light"}
                              size={"medium"}
                              loadingText={""}
                            />
                          </div>
                        }
                      >
                        <CallPlayer
                          recordingId={parseInt(recordingId!)}
                          enableRegions={true}
                          showSkipSilenceBtn={true}
                          setRegionSelection={setRegionSelection}
                          setPlaybackProgress={setPlaybackProgress}
                          setRecordingDuration={setRecordingDuration}
                          highlightCommentRegion={highlightCommentRegion}
                          trackSpanTime={trackSpanTime}
                          setAudioLoaded={setAudioLoaded}
                          commentRegions={commentRegions}
                          lockedRegions={lockedRegions}
                          setLockedRegions={setLockedRegions}
                          onUserSeekOutOfCommentRegion={
                            onUserSeekOutOfCommentRegion
                          }
                          setAudioLoadedErrored={setAudioLoadedErrored}
                          recordingTrackLineClicked={recordingTrackLineClicked}
                          wavformContainerId="callDetailsPage"
                          durationInSeconds={recordingDuration}
                          segments={participantsTrackInsight}
                          setSelectedTab={setSelectedTab}
                          shared={shared}
                          baseLicenseId={
                            callDetail?.recordings[0].baseLicenseId
                          }
                          isDiscarded={callDetail?.status === "Discarded"}
                          highlightComment={highlightComment}
                          commentsShared={commentShared}
                          waveform={waveform}
                          isFromDetails={true}
                          setDownloadDisabled={setDownloadDisabled}
                        />
                      </Suspense>
                    </ErrorBoundary>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-12">
                  <div className="top-player mb-2 p-t-5 p-r-18 p-l-15 border-black-2 border-bottom-solid border-bottom-w-1">
                    {((audioLoaded && !audioLoadedErrored) ||
                      callDetail?.status === "Discarded") && (
                      <div
                        className={`${showUpgradeLicense ? "blurArea3" : ""}`}
                      >
                        <RecordingTrack
                          trackLabel={`${trans.fetchLabelKeyTranslation(
                            "LabelParticipants",
                            "Participation"
                          )}`}
                          duration={recordingDuration}
                          segments={participantsTrackInsight}
                          onTrackSpanPlay={onRecordingTrackSpanPlay}
                          error={rawTranscriptError}
                          isAIAnalysed={callDetail?.recordings[0].isAIAnalysed}
                          isDiscarded={isDiscarded}
                        />
                        <RecordingTrack
                          trackLabel={`${trans.fetchLabelKeyTranslation(
                            "LabelExactMatchTopics",
                            "Exact Match Topics"
                          )}`}
                          duration={recordingDuration}
                          segments={exactMatchTopicInsights}
                          onTrackSpanPlay={onRecordingTrackSpanPlay}
                          error={recTopicsError}
                          isAIAnalysed={callDetail?.recordings[0].isAIAnalysed}
                          isDiscarded={isDiscarded}
                        />
                        <RecordingTrack
                          trackLabel={`${trans.fetchLabelKeyTranslation(
                            "TextSentiments",
                            "Sentiments"
                          )}`}
                          duration={recordingDuration}
                          segments={sentimentsTrackInsight}
                          onTrackSpanPlay={onRecordingTrackSpanPlay}
                          error={recSentimentsError}
                          isAIAnalysed={callDetail?.recordings[0].isAIAnalysed}
                          isDiscarded={isDiscarded}
                        />
                      </div>
                    )}
                    {!audioLoaded &&
                      !audioLoadedErrored &&
                      callDetail?.status !== "Discarded" && (
                        <div className="d-flex align-items-center justify-content-center">
                          <Loader type={"infinite-spinner"} />
                        </div>
                      )}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-12">
                  <div className="settingData k-custom-tab p-t-5 p-b-20">
                    <TabStrip
                      tabPosition={"left"}
                      selected={selectedTab}
                      onSelect={handleSelect}
                    >
                      <TabStripTab
                        title={`${trans.fetchLabelKeyTranslation(
                          "TranscriptTabTitle",
                          "Transcript"
                        )}`}
                      >
                        <TranscriptionTab
                          rawTranscript={rawTranscript}
                          rawTranscriptError={rawTranscriptError}
                          sentimentsTrackInsight={sentimentsTrackInsight}
                          playbackProgress={playbackProgress}
                          secondParties={callDetail?.secondParties}
                          callDatetime={callDetail?.callTime}
                          isAIAnalysed={!showUpgradeLicense}
                          shared={shared}
                          fetchLabelKeyTranslation={
                            trans.fetchLabelKeyTranslation
                          }
                          lockedSegmentCount={
                            callDetail?.recordings[0]?.lockedSegments?.length ??
                            0
                          }
                          summary={summary}
                          recordingId={parseInt(recordingId!)}
                          actions={actions}
                          playTranscription={playTranscription}
                        />
                      </TabStripTab>
                      {commentShared && (
                        <TabStripTab
                          title={`${trans.fetchLabelKeyTranslation(
                            "CommentsTabTitle",
                            "Comments"
                          )}`}
                        >
                          <RecordingComments
                            comments={recordingComments}
                            error={recordingCommentsError}
                            recordingId={parseInt(recordingId!)}
                            regionSelection={regionSelection}
                            recordingDuration={recordingDuration}
                            onCommentMouseOver={onCommentMouseOver}
                            onCommentMouseOut={onCommentMouseOut}
                            audioLoaded={audioLoaded}
                            disableCommentPlay={audioLoaded === false}
                            onAddComment={onAddComment}
                            onDeleteComment={onDeleteComment}
                            onUpdateComment={onUpdateComment}
                            shared={shared}
                            platformCallDetailId={platformCallDetailId}
                            recordingPath={
                              window.location.pathname + window.location.search
                            }
                            baseLicenseId={
                              callDetail?.recordings[0].baseLicenseId
                            }
                            totalCommentsTemp={totalCommentsTemp}
                            setTotalCommentsTemp={setTotalCommentsTemp}
                            highlightedCommentId={highlightedCommentId}
                          />
                        </TabStripTab>
                      )}
                      {evalShared && (
                        <TabStripTab
                          title={`${trans.fetchLabelKeyTranslation(
                            "SCTabTitle",
                            "Evaluation"
                          )}`}
                          disabled={isDiscarded}
                        >
                          {MemoizedRecordingScorecardTab}
                        </TabStripTab>
                      )}
                      {outcomeShared && (
                        <TabStripTab
                          title={`${trans.fetchLabelKeyTranslation(
                            "TitleCallDetail",
                            "Call Outcomes"
                          )}`}
                          disabled={isDiscarded}
                        >
                          {callDetailLoading && (
                            <ContentPlaceholder>
                              <Loader type={"infinite-spinner"} />
                            </ContentPlaceholder>
                          )}
                          {callDetailError && (
                            <ContentPlaceholder>
                              <span>
                                <i className="bi bi-exclamation-triangle-fill tx-amber"></i>{" "}
                                {`${trans.fetchLabelKeyTranslation(
                                  "CallDetailLoadFailedMsg",
                                  "Uh Oh! Something went wrong. Please try again... "
                                )}`}
                              </span>
                            </ContentPlaceholder>
                          )}
                          {callDetail && (
                            <CallSummary
                              callDetail={callDetail}
                              shared={shared}
                              onUpdateCallDetailProperties={
                                onUpdateCallDetailProperties
                              }
                            />
                          )}
                        </TabStripTab>
                      )}
                      {participantHistoryShared && (
                        <TabStripTab
                          title={`${trans.fetchLabelKeyTranslation(
                            "CallHistoryTabTitle",
                            "Participant History"
                          )}`}
                          contentClassName="participant-active"
                        >
                          <CallHistory
                            secondParties={callDetail?.secondParties}
                            shared={shared}
                            callDetail={callDetail}
                            setAlertData={setAlertData}
                          />
                        </TabStripTab>
                      )}
                      {/* PLEASE DO NOT DELETE. UNCOMMENT TO SHOW ANALYTICS TAB */}
                      {/* {callDetail?.platform.id === 5 && (
                        <TabStripTab title={<IcsAnalytics tab />}>
                          <div className="callLegs">
                            <div className="row">
                              <div className="col-md-12 m-b-15">
                                <div className="card cardEffect">
                                  <div className="card-header d-flex justify-content-between align-items-center p-t-7 p-b-8">
                                    <div className="titleText">
                                      <i className="bi bi-people-fill p-r-5"></i>
                                      <span className="fs-13">
                                        Call Details
                                      </span>
                                    </div>
                                  </div>
                                  <div className="card-body p-0">
                                    <div className="kpiBody">
                                      <div className="kpi-row">
                                        <div className="kpi-value-details">
                                          <div className="kpi-left-info">
                                            <div className="kpi-row-info">
                                              <div className="kpi-text">
                                                User
                                              </div>
                                              <div className="kpi-value">
                                                <label id="lblUser">
                                                  Clarissa Wind
                                                </label>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="kpi-right-info">
                                            <div className="kpi-row-info">
                                              <div className="kpi-text">
                                                Number
                                              </div>
                                              <div className="kpi-value">
                                                <label id="lblNumber">
                                                  +441895478899
                                                </label>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="kpi-row">
                                        <div className="kpi-value-details">
                                          <div className="kpi-left-info">
                                            <div className="kpi-row-info">
                                              <div className="kpi-text">
                                                DDI
                                              </div>
                                              <div className="kpi-value">
                                                <label id="lblDDI">
                                                  Serial Routing Group
                                                </label>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="kpi-right-info">
                                            <div className="kpi-row-info">
                                              <div className="kpi-text">
                                                Wait Time
                                              </div>
                                              <div className="kpi-value">
                                                <label id="lblWaitTime">
                                                  00:00:20
                                                </label>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-md-12">
                                <div className="card cardEffect">
                                  <div className="card-header d-flex justify-content-between align-items-center p-t-5 p-b-6">
                                    <div className="titleText">
                                      <i className="bi bi-diagram-2-fill fs-20 p-r-5"></i>
                                      <span className="fs-13">Call Legs</span>
                                    </div>
                                  </div>
                                  <div className="card-body p-0">
                                    <div className="tableList rowSpace float-left w-100">
                                      <div className="tableMain">
                                        <Grid
                                          style={{
                                            height: "420px",
                                            overflowY: "auto",
                                          }}
                                          data={products}
                                        >
                                          <GridColumn
                                            field="ProductID"
                                            title="User"
                                            cell={CustomCellUser}
                                            width="220px"
                                          />
                                          <GridColumn
                                            field="ProductDate"
                                            title="Date"
                                          />
                                          <GridColumn
                                            field="ProductTime"
                                            title="Time"
                                            // width="120px"
                                          />
                                          <GridColumn
                                            field="ProductDDI"
                                            title="DDI"
                                          />
                                          <GridColumn
                                            field="ProductNumber"
                                            title="Number"
                                          />
                                          <GridColumn
                                            field="ProductDuration"
                                            title="Duration"
                                          />
                                          <GridColumn
                                            field="ProductOutcome"
                                            title="Outcome"
                                          />
                                          <GridColumn
                                            field="ProductPresence"
                                            title="Presence"
                                          />
                                        </Grid>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </TabStripTab>
                      )} */}
                      {auditLogsShared && checkForShowAuditLogs() && (
                        <TabStripTab
                          title={`${trans.fetchLabelKeyTranslation(
                            "PlaybackAuditTabTitle",
                            "Playback Audit"
                          )}`}
                        >
                          <AuditLogSettings
                            callDetail={callDetail}
                            title={false}
                            filters={false}
                            search={false}
                            excludedColumns={[
                              "EntityType",
                              "entityFriendlyName",
                            ]}
                            shared={shared}
                          />
                        </TabStripTab>
                      )}
                    </TabStrip>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CallDetails;
